/**
 * 퀘스트 쿠폰 — 광장 큰 TV (완료 과시·축하)
 * ============================================
 * 설계: docs/2026-06-08-quest-coupon-design.md §6.2 / dev_steps P5
 *
 * 흐름: 회원 QR 연속 스캔 → POST /tools/quest/status {member_qr}
 *   - completed=true:  풀스크린 "🎉 퀘스트 완료~! {coupon_name} 획득!" + 축하 애니메이션
 *   - completed=false 且 active_set: "아직 N개 남았어~!" (N=remaining)
 *   - active_set=false: "진행 중인 퀘스트가 없습니다"
 *
 * 연출: 큰 TV 출력 가정 — 대형 폰트 + 색종이(confetti) 풀스크린.
 *   축하 애니메이션은 스캔당 1회 재생(설계 확정) — animationKey 로 강제 리마운트.
 *
 * 카메라 패턴: QrStickerReaderPage(카메라) = QRScanner 컴포넌트 재사용.
 *   QRScanner 가 내부에서 파싱까지 수행 → onScan 은 추출값(JRG_/ZLB_/ZLP_)을 받음.
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { isBridgeApp } from '../utils/isBridgeApp';
import { extractQuestScanCode } from '../utils/qrUtils';
import api, {
  fetchQuestMissionMap,
  getMemberByQR,
  getQuestSetup,
  getQuestStatus,
} from '../services/api';
import { canRenderStatusMap } from '../components/quest/MissionStatusMap';
import headquartersMissionCompleteBackground from '../assets/quest/headquarters/mission-complete-background-1x.png';
import headquartersMissionCompleteLogo from '../assets/quest/headquarters/mission-complete-logo-v2.png';
import headquartersRewardBackground from '../assets/quest/headquarters/reward-background-1x.png';
import headquartersRewardSpeechBubble from '../assets/quest/headquarters/reward-speech-bubble-1x.png';
import headquartersRewardCoin from '../assets/quest/headquarters/reward-coin-1x.png';
import headquartersRewardCoupon from '../assets/quest/headquarters/reward-coupon-v2-1x.png';
import headquartersRewardEgg from '../assets/quest/headquarters/reward-egg-1x.png';
import headquartersReceiveGiftButton from '../assets/quest/headquarters/reward-button-receive-1x.png';
import headquartersAgainButton from '../assets/quest/headquarters/reward-button-again-1x.png';
import headquartersConfirmButton from '../assets/quest/headquarters/reward-button-confirm-1x.png';
import {
  resolveQuestAnimalReveal,
  shouldPresentQuestAnimalReward,
} from '../constants/questAnimalReveals';
import { alignHeadquartersMap, headquartersMapImageStyle, headquartersMapPoint } from '../utils/headquartersMapGeometry';
import './QuestStatusPage.css';

const COOLDOWN_MS = 1500; // 같은 회원 QR 연속 스캔 차단
const RESULT_DISPLAY_MS = 6000; // 연출 노출 시간 → 이후 대기 화면 복귀
const REWARD_BUTTON_PRESS_MS = 220;
const MISSION_COMPLETE_INTRO_FALLBACK_MS = 6500;
const CUBE_ROTATION_FALLBACK_MS = 6000; // 약 4.5초 영상이 멈춰도 은화 연출로 이어지게 한다.
const SILVER_REWARD_FALLBACK_MS = 6000;
const COUPON_REVEAL_FALLBACK_MS = 6000;
const EGG_HATCH_FALLBACK_MS = 9000; // 약 6초 원본 재생을 우선하고, 종료 이벤트 누락만 안전 처리한다.
const MISSION_MAP_DISPLAY_MS = 30000; // 조작 중인 아이가 충분히 확인하도록, 마지막 동작 후 30초
const MISSION_MARKER_CLUSTER_DISTANCE_PX = 28; // 고객 앱과 같은 지도 원본 좌표 근접 기준
const CONFETTI_COUNT = 40; // 축하 색종이 조각 수
const HEADQUARTERS_SETUP_REFRESH_MS = 60000; // 활성 세트·대기화면 교체를 장시간 실행 중에도 반영

const QUEST_TEST_MEMBERS = {
  incomplete: 'MEM_20260804_000002',
};
const QUEST_TEST_QR_STORAGE_KEYS = {
  incomplete: 'quest_status_test_qr_incomplete',
  complete: 'quest_status_test_qr_complete',
};
export const LIVE_TOOLS_HOST = 'zl-tools.nxithub.com';
export const HOME_EXIT_TAP_COUNT = 5;
export const HOME_EXIT_TAP_WINDOW_MS = 3000;

export function isQuestDeveloperHost(hostname) {
  return String(hostname || '').toLowerCase() !== LIVE_TOOLS_HOST;
}

const SHOW_QUEST_TEST_CONTROLS = typeof window !== 'undefined'
  && isQuestDeveloperHost(window.location.hostname);

function rememberQuestTestQr(kind, qrCode) {
  if (!SHOW_QUEST_TEST_CONTROLS || !QUEST_TEST_QR_STORAGE_KEYS[kind] || !qrCode) return;
  localStorage.setItem(QUEST_TEST_QR_STORAGE_KEYS[kind], qrCode);
}

const IDLE_SCREEN = {
  id: 'idle',
  image: null,
  alt: '주키퍼 본부 대기 화면',
};

/** Tools 셋업 응답에서 CRM이 저장한 주키퍼 본부 대기화면 URL만 안전하게 꺼낸다. */
export function extractHeadquartersWaitingScreenUrl(setup) {
  const candidate = setup?.active_set?.reward_presentation?.waiting_screen_image_url;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : '';
}

/** 셋업 응답의 세트별 주키퍼 본부 전용 지도(최대 2층)만 렌더 가능한 형태로 고른다. */
export function extractHeadquartersMaps(setup) {
  const source = setup?.active_set?.reward_presentation?.headquarters_maps;
  if (!Array.isArray(source)) return [];
  const floors = new Set();
  return source
    .filter((item) => {
      const floor = normalizeFloor(item?.floor);
      if (!floor || floors.has(floor) || typeof item?.image_url !== 'string' || !item.image_url.trim()) {
        return false;
      }
      floors.add(floor);
      return true;
    })
    .slice(0, 2)
    .map((item) => ({
      ...item,
      floor: normalizeFloor(item.floor),
      image_url: item.image_url.trim(),
    }))
    .sort((a, b) => a.floor - b.floor);
}

/** 전용 지도가 있으면 일반 지도의 배경 설정은 유지하고 층 지도만 세트 자산으로 교체한다. */
export function withHeadquartersMapOverride(mapConfig, headquartersMaps, storeId = localStorage.getItem('store_id')) {
  const generic = mapConfig && typeof mapConfig === 'object' ? mapConfig : {};
  return {
    background: generic.background || null,
    maps: Array.isArray(headquartersMaps) && headquartersMaps.length > 0
      ? headquartersMaps.map(art => alignHeadquartersMap(art, (generic.maps || []).find(map => normalizeFloor(map.floor_key ?? map.floor) === normalizeFloor(art.floor)), storeId))
      : (Array.isArray(generic.maps) ? generic.maps : []),
  };
}

const REWARD_FLOW = [
  {
    id: 'mission-complete',
    image: headquartersMissionCompleteBackground,
    headlineImage: headquartersMissionCompleteLogo,
    heroKind: 'complete-character',
    buttonImage: headquartersReceiveGiftButton,
    alt: '주키퍼 본부 미션 완료 화면',
    actionLabel: '선물 받기',
  },
  {
    id: 'gift-coin',
    image: headquartersRewardBackground,
    dynamicHeadline: 'coin',
    heroImage: headquartersRewardCoin,
    heroKind: 'coin',
    tada: true,
    buttonImage: headquartersAgainButton,
    alt: '주키퍼 본부 주렁주화 선물 화면',
    actionLabel: '한번 더',
  },
  {
    id: 'gift-coupon',
    image: headquartersRewardBackground,
    dynamicHeadline: 'coupon',
    heroImage: headquartersRewardCoupon,
    heroKind: 'coupon',
    tada: true,
    buttonImage: headquartersAgainButton,
    alt: '주키퍼 본부 무료 쿠폰 선물 화면',
    actionLabel: '한번 더',
  },
  {
    id: 'gift-friend',
    image: headquartersRewardBackground,
    dynamicHeadline: 'friend',
    heroImage: headquartersRewardEgg,
    heroKind: 'egg',
    tada: true,
    buttonImage: headquartersConfirmButton,
    alt: '주키퍼 본부 동물친구 선물 화면',
    actionLabel: '확인',
    waitForConfirmation: true,
  },
];

const EMPTY_REWARD_DETAILS = Object.freeze({
  coinAmount: null,
  couponName: '',
  animalRewardStatus: '',
  questRunId: null,
  memberAnimalProfileId: null,
  animalProfileId: null,
  animalId: '',
  animalName: '',
  animalImageUrl: '',
  animalVideoUrl: '',
  missionCompleteVideoUrl: '',
  rewardBoxVideoUrl: '',
  coinSilverVideoUrl: '',
  couponVideoUrl: '',
  eggHatchVideoUrl: '',
});

function toPositiveInteger(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
}

function extractRewardDetails(payload) {
  const sources = [];
  const appendSource = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) sources.push(value);
  };

  appendSource(payload);
  appendSource(payload?.active_set);
  appendSource(payload?.quest_set);
  appendSource(payload?.reward);
  appendSource(payload?.rewards);
  if (Array.isArray(payload?.rewards)) payload.rewards.forEach(appendSource);

  // 백엔드 동물 보상 응답은 animal_reward.animal_profile 형태를 우선 수용한다.
  sources.slice().forEach((source) => {
    appendSource(source.animal_reward);
    appendSource(source.animal_profile);
    appendSource(source.animal_reward?.animal_profile);
  });

  let coinAmount = null;
  let couponName = '';
  let animalRewardStatus = '';
  let questRunId = null;
  let memberAnimalProfileId = null;
  let animalProfileId = null;
  let animalId = '';
  let animalName = '';
  let animalImageUrl = '';
  let animalVideoUrl = '';
  let missionCompleteVideoUrl = '';
  let rewardBoxVideoUrl = '';
  let coinSilverVideoUrl = '';
  let couponVideoUrl = '';
  let eggHatchVideoUrl = '';
  sources.forEach((source) => {
    if (coinAmount === null) {
      coinAmount = toPositiveInteger(source.reward_coin_amount ?? source.coin_amount);
      if (coinAmount === null && ['coin', 'both'].includes(source.reward_type)) {
        coinAmount = toPositiveInteger(source.amount);
      }
    }

    if (!couponName) {
      const candidate = source.reward_coupon_name ?? source.coupon_name
        ?? (['coupon', 'both'].includes(source.reward_type) ? source.name : '');
      if (typeof candidate === 'string' && candidate.trim()) couponName = candidate.trim();
    }

    if (!animalId) {
      const candidate = source.reward_animal_id
        ?? source.animal_id
        ?? source.animal_code
        ?? source.animal_key;
      if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
        animalId = String(candidate).trim();
      }
    }

    if (!animalRewardStatus && typeof source.animal_reward_status === 'string') {
      animalRewardStatus = source.animal_reward_status.trim();
    }
    if (questRunId === null) questRunId = toPositiveInteger(source.quest_run_id);
    if (memberAnimalProfileId === null) {
      memberAnimalProfileId = toPositiveInteger(source.member_animal_profile_id);
    }
    if (animalProfileId === null) {
      const candidate = source.animal_profile_id
        ?? ((source.animal_id !== null && source.animal_id !== undefined)
          || source.name_ko
          || source.image_url
          ? source.id
          : null);
      animalProfileId = toPositiveInteger(candidate);
    }

    if (!animalName) {
      const candidate = source.reward_animal_name
        ?? source.animal_name
        ?? source.name_ko;
      if (typeof candidate === 'string' && candidate.trim()) animalName = candidate.trim();
    }

    if (!animalImageUrl && typeof source.image_url === 'string' && source.image_url.trim()) {
      animalImageUrl = source.image_url.trim();
    }

    if (
      !animalVideoUrl
      && source.reward_presentation
      && typeof source.reward_presentation.video_url === 'string'
      && source.reward_presentation.video_url.trim()
    ) {
      animalVideoUrl = source.reward_presentation.video_url.trim();
    }

    const presentation = source.reward_presentation;
    if (presentation && typeof presentation === 'object') {
      if (
        !missionCompleteVideoUrl
        && typeof presentation.mission_complete_video_url === 'string'
        && presentation.mission_complete_video_url.trim()
      ) {
        missionCompleteVideoUrl = presentation.mission_complete_video_url.trim();
      }
      if (
        !rewardBoxVideoUrl
        && typeof presentation.reward_box_video_url === 'string'
        && presentation.reward_box_video_url.trim()
      ) {
        rewardBoxVideoUrl = presentation.reward_box_video_url.trim();
      }
      if (
        !coinSilverVideoUrl
        && typeof presentation.coin_silver_video_url === 'string'
        && presentation.coin_silver_video_url.trim()
      ) {
        coinSilverVideoUrl = presentation.coin_silver_video_url.trim();
      }
      if (
        !couponVideoUrl
        && typeof presentation.coupon_video_url === 'string'
        && presentation.coupon_video_url.trim()
      ) {
        couponVideoUrl = presentation.coupon_video_url.trim();
      }
      if (
        !eggHatchVideoUrl
        && typeof presentation.egg_hatch_video_url === 'string'
        && presentation.egg_hatch_video_url.trim()
      ) {
        eggHatchVideoUrl = presentation.egg_hatch_video_url.trim();
      }
    }
  });

  return {
    coinAmount,
    couponName,
    animalRewardStatus,
    questRunId,
    memberAnimalProfileId,
    animalProfileId,
    animalId,
    animalName,
    animalImageUrl,
    animalVideoUrl,
    missionCompleteVideoUrl,
    rewardBoxVideoUrl,
    coinSilverVideoUrl,
    couponVideoUrl,
    eggHatchVideoUrl,
  };
}

function mergeRewardDetails(...details) {
  return details.reduce((merged, next) => ({
    coinAmount: next?.coinAmount ?? merged.coinAmount,
    couponName: next?.couponName || merged.couponName,
    animalRewardStatus: next?.animalRewardStatus || merged.animalRewardStatus,
    questRunId: next?.questRunId ?? merged.questRunId,
    memberAnimalProfileId: next?.memberAnimalProfileId ?? merged.memberAnimalProfileId,
    animalProfileId: next?.animalProfileId ?? merged.animalProfileId,
    animalId: next?.animalId || merged.animalId,
    animalName: next?.animalName || merged.animalName,
    animalImageUrl: next?.animalImageUrl || merged.animalImageUrl,
    animalVideoUrl: next?.animalVideoUrl || merged.animalVideoUrl,
    missionCompleteVideoUrl: next?.missionCompleteVideoUrl || merged.missionCompleteVideoUrl,
    rewardBoxVideoUrl: next?.rewardBoxVideoUrl || merged.rewardBoxVideoUrl,
    coinSilverVideoUrl: next?.coinSilverVideoUrl || merged.coinSilverVideoUrl,
    couponVideoUrl: next?.couponVideoUrl || merged.couponVideoUrl,
    eggHatchVideoUrl: next?.eggHatchVideoUrl || merged.eggHatchVideoUrl,
  }), { ...EMPTY_REWARD_DETAILS });
}

function rewardHeadline(screen, rewardDetails) {
  if (screen.dynamicHeadline === 'coin') {
    return rewardDetails.coinAmount
      ? `주렁주화 ${rewardDetails.coinAmount.toLocaleString('ko-KR')}개!`
      : '주렁주화 보상!';
  }
  if (screen.dynamicHeadline === 'coupon') {
    const name = rewardDetails.couponName || '쿠폰 보상';
    return /[!！]$/.test(name) ? name : `${name}!`;
  }
  if (screen.dynamicHeadline === 'friend') {
    return '동물친구 획득!';
  }
  return '';
}



function normalizeFloor(value) {
  const match = String(value ?? '').match(/\d+/);
  return match ? Number(match[0]) : null;
}

function asMapRatio(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : null;
}

function missionRewardLabel(spot) {
  if (spot.reward_type === 'coin'
    && spot.reward_coin_amount !== null
    && spot.reward_coin_amount !== undefined
    && Number.isFinite(Number(spot.reward_coin_amount))) {
    return `주렁주화 ${Number(spot.reward_coin_amount)}개`;
  }
  if (spot.reward_type === 'coupon' && spot.reward_coupon_name) {
    return spot.reward_coupon_name;
  }
  return null;
}

/**
 * 같은 층 지도에서 28px 이내인 미션들을 하나의 대표 핀으로 묶는다.
 * 화면 확대율이 아니라 지도 원본 좌표를 기준으로 하므로 드래그·확대 중에도 그룹이 흔들리지 않는다.
 * A-B, B-C가 가까우면 A-C가 28px를 조금 넘어도 한 장소의 연결 그룹으로 취급한다.
 */
function clusterMissionMarkers(spots, mapWidth, mapHeight, mapScale = 1) {
  const items = spots || [];
  const screenScale = Math.max(0.001, Number(mapScale) || 1);
  const parents = items.map((_, index) => index);
  const find = (index) => {
    let root = index;
    while (parents[root] !== root) root = parents[root];
    while (parents[index] !== index) {
      const next = parents[index];
      parents[index] = root;
      index = next;
    }
    return root;
  };
  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parents[rootB] = rootA;
  };

  for (let a = 0; a < items.length; a += 1) {
    for (let b = a + 1; b < items.length; b += 1) {
      const dx = (items[a].x - items[b].x) * mapWidth * screenScale;
      const dy = (items[a].y - items[b].y) * mapHeight * screenScale;
      if (Math.hypot(dx, dy) <= MISSION_MARKER_CLUSTER_DISTANCE_PX) union(a, b);
    }
  }

  const groups = new Map();
  items.forEach((item, index) => {
    const root = find(index);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(item);
  });

  return [...groups.values()].map((missions) => {
    const firstIncomplete = missions.find((mission) => !mission.done);
    const representative = firstIncomplete || missions[0];
    return {
      ...representative,
      marker_key: missions.map((mission) => mission.spot_id).join('-'),
      name: missions.length > 1 ? `미션 ${missions.length}개` : representative.name,
      done: missions.every((mission) => mission.done),
      x: missions.reduce((sum, mission) => sum + mission.x, 0) / missions.length,
      y: missions.reduce((sum, mission) => sum + mission.y, 0) / missions.length,
      missions,
      cluster_count: missions.length,
      dx: 0,
      dy: 0,
    };
  });
}



function ChevronIcon({ direction }) {
  const path = direction === 'left' ? 'M15.5 4L7.5 12l8 8' : 'M8.5 4l8 8-8 8';
  return (
    <svg className="qs-icon-chevron" viewBox="0 0 24 24" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

/** 고객 앱 progress/maps 응답을 주키퍼 본부의 한 화면 모델로 바꾼다. */
function createInteractiveMissionMap(progress, mapConfig, memberId) {
  const questSets = Array.isArray(progress?.quest_sets) ? progress.quest_sets : [];
  const questSet = questSets.find((item) => String(item.quest_set_status).toLowerCase() === 'active')
    || questSets[0];
  if (!questSet || !Array.isArray(questSet.spots) || questSet.spots.length === 0) return null;

  const mapByFloor = new Map();
  (mapConfig?.maps || []).forEach((map) => {
    const floor = normalizeFloor(map.floor_key ?? map.floor);
    if (floor && map.image_url) mapByFloor.set(floor, map);
  });

  const spots = questSet.spots.map((spot, index) => {
    const memberProgress = (spot.members || []).find((member) => member.member_id === memberId);
    const floor = normalizeFloor(spot.floor) || 4;
    const mappedSpot = {
      spot_id: spot.spot_id,
      name: spot.spot_name || `미션 ${index + 1}`,
      description: spot.spot_description || '미션 장소를 찾아 QR을 스캔해 보세요.',
      image_url: spot.mission_image_url || null,
      reward_label: missionRewardLabel(spot),
      done: Boolean(memberProgress?.is_completed),
      completed_at: memberProgress?.tagged_at || memberProgress?.completed_at || null,
      floor,
      x: asMapRatio(spot.map_norm_x)
        ?? (spot.map_x !== null && spot.map_x !== undefined && Number.isFinite(Number(spot.map_x))
          ? Number(spot.map_x) / 1280
          : null),
      y: asMapRatio(spot.map_norm_y)
        ?? (spot.map_y !== null && spot.map_y !== undefined && Number.isFinite(Number(spot.map_y))
          ? Number(spot.map_y) / 956
          : null),
      sort_order: Number(spot.sort_order) || index,
    };
    return { ...mappedSpot, ...headquartersMapPoint(mapByFloor.get(floor), mappedSpot.x, mappedSpot.y) };
  }).sort((a, b) => a.sort_order - b.sort_order);

  const floors = [...new Set(spots.map((spot) => spot.floor))]
    .filter((floor) => mapByFloor.has(floor))
    .sort((a, b) => a - b);
  if (floors.length === 0) return null;

  return {
    interactive: true,
    questSetName: questSet.quest_set_name || '주렁주렁 미션',
    memberName: (progress.members || []).find((member) => member.member_id === memberId)?.nickname
      || (progress.members || []).find((member) => member.member_id === memberId)?.name
      || '',
    done: spots.filter((spot) => spot.done).length,
    total: spots.length,
    spots,
    floors,
    maps: Object.fromEntries(floors.map((floor) => [floor, mapByFloor.get(floor)])),
  };
}

function MissionProgressPin({ done, count = 1, missions = [], selectedMissionId = null }) {
  const selectedIndex = selectedMissionId === null
    ? -1
    : missions.findIndex((mission) => String(mission.spot_id) === selectedMissionId);

  return (
    <>
      {selectedIndex >= 0 && missions.length > 1 && (
        <span className="qs-progress-pin-cluster-tabs" aria-hidden="true">
          {missions.map((mission, index) => (
            <span
              key={mission.spot_id}
              className={`${index === selectedIndex ? 'is-active' : ''}${mission.done ? ' is-complete' : ''}`}
            />
          ))}
        </span>
      )}
      <span className="qs-progress-pin-card">
        <span className="qs-progress-pin-glyph">
          {done ? (
            <svg viewBox="0 0 20 14" aria-hidden="true">
              <path
                d="M1.8 6.6l5.2 5.2L18.2 1.6"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <span className="qs-progress-pin-z" aria-hidden="true">Z</span>
          )}
        </span>
      </span>
      <span className="qs-progress-pin-tail" aria-hidden="true" />
      {count > 1 && selectedIndex < 0 && <span className="qs-progress-pin-count">{count}</span>}
    </>
  );
}

function MissionProgressScreen({
  data,
  onMapImageChange,
  uprightPreview,
  onActivity,
}) {
  const initialFloor = data.interactive ? data.floors[0] : normalizeFloor(data.floor) || 4;
  const [activeFloor, setActiveFloor] = useState(initialFloor);
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const [mapTransform, setMapTransform] = useState({ x: 0, y: 0, scale: 1 });
  const mapViewportRef = useRef(null);
  const mapCanvasRef = useRef(null);
  const carouselRef = useRef(null);
  const transformRef = useRef(mapTransform);
  const dragRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const didDragRef = useRef(false);
  const pendingTransformRef = useRef(null);
  const transformFrameRef = useRef(null);

  const floors = data.interactive ? data.floors : [initialFloor];
  const activeMap = data.interactive ? data.maps[activeFloor] : data.map;
  useEffect(() => {
    onMapImageChange(activeMap?.image_url || null);
  }, [activeMap?.image_url, onMapImageChange]);
  const mapWidth = Number(activeMap?.width) || 1280;
  const mapHeight = Number(activeMap?.height) || 956;
  const floorSpots = useMemo(
    () => (data.spots || []).filter((spot) => normalizeFloor(spot.floor ?? activeFloor) === activeFloor),
    [activeFloor, data.spots]
  );
  const renderableSpots = useMemo(
    () => clusterMissionMarkers(
      floorSpots.filter((spot) => spot.x !== null && spot.y !== null),
      mapWidth,
      mapHeight,
      mapTransform.scale
    ),
    [floorSpots, mapHeight, mapTransform.scale, mapWidth]
  );
  const floorNavigationSpots = useMemo(() => {
    const locatedSpots = floorSpots.filter((spot) => spot.x !== null && spot.y !== null);
    const clusteredSpots = clusterMissionMarkers(locatedSpots, mapWidth, mapHeight, 1)
      .flatMap((cluster) => cluster.missions);
    const clusteredIds = new Set(clusteredSpots.map((spot) => String(spot.spot_id)));
    return [
      ...clusteredSpots,
      ...floorSpots.filter((spot) => !clusteredIds.has(String(spot.spot_id))),
    ];
  }, [floorSpots, mapHeight, mapWidth]);
  const selectedIndex = floorSpots.findIndex((spot) => String(spot.spot_id) === selectedMissionId);
  const selectedMission = selectedIndex >= 0 ? floorSpots[selectedIndex] : null;
  const selectedNavigationIndex = floorNavigationSpots.findIndex(
    (spot) => String(spot.spot_id) === selectedMissionId
  );
  useEffect(() => {
    transformRef.current = mapTransform;
  }, [mapTransform]);

  const getMapMetrics = useCallback(() => {
    const viewport = mapViewportRef.current;
    const canvas = mapCanvasRef.current;
    const viewportWidth = viewport?.clientWidth || 600;
    const viewportHeight = viewport?.clientHeight || 900;
    const canvasWidth = canvas?.clientWidth || viewportWidth * 0.94;
    const canvasHeight = canvas?.clientHeight || canvasWidth * (mapHeight / mapWidth);
    return {
      viewportWidth,
      viewportHeight,
      canvasWidth,
      canvasHeight,
      baseLeft: (viewportWidth - canvasWidth) / 2,
      baseTop: (viewportHeight - canvasHeight) / 2,
    };
  }, [mapHeight, mapWidth]);

  const constrainTransform = useCallback((next, metrics = getMapMetrics()) => {
    const scale = Math.min(2.4, Math.max(1, Number(next.scale) || 1));
    const {
      viewportWidth,
      viewportHeight,
      canvasWidth,
      canvasHeight,
      baseLeft,
      baseTop,
    } = metrics;
    // 확대 상태에서는 지도 가장자리가 화면 안쪽에서 잘려 보이지 않도록
    // 캔버스가 뷰포트를 덮는 범위 안에서만 이동시킨다. 기본 배율에서는
    // 중앙 정렬값(0, 0)을 유지한다.
    const scaledWidth = canvasWidth * scale;
    const scaledHeight = canvasHeight * scale;
    const restingPan = scale <= 1.001 ? 56 : 0;
    const minX = Math.min(0, viewportWidth - baseLeft - scaledWidth - restingPan);
    const maxX = Math.max(0, -baseLeft + restingPan);
    const minY = Math.min(0, viewportHeight - baseTop - scaledHeight - restingPan);
    const maxY = Math.max(0, -baseTop + restingPan);
    return {
      x: Math.min(maxX, Math.max(minX, Number(next.x) || 0)),
      y: Math.min(maxY, Math.max(minY, Number(next.y) || 0)),
      scale,
    };
  }, [getMapMetrics]);

  const setTransform = useCallback((next) => {
    const value = constrainTransform(next);
    if (transformFrameRef.current !== null) {
      window.cancelAnimationFrame(transformFrameRef.current);
      transformFrameRef.current = null;
    }
    pendingTransformRef.current = null;
    transformRef.current = value;
    setMapTransform(value);
  }, [constrainTransform]);

  const paintScheduledTransform = useCallback((value) => {
    const canvas = mapCanvasRef.current;
    if (!canvas) return;
    canvas.style.transform = `translate3d(${value.x}px, ${value.y}px, 0) scale(${value.scale})`;
    canvas.style.setProperty(
      '--qs-map-inverse-scale',
      String((1 + Math.max(0, value.scale - 1) * 0.16) / Math.max(value.scale, 0.001))
    );
  }, []);

  const scheduleTransform = useCallback((next, metrics) => {
    const value = constrainTransform(next, metrics);
    transformRef.current = value;
    pendingTransformRef.current = value;
    if (transformFrameRef.current !== null) return;
    transformFrameRef.current = window.requestAnimationFrame(() => {
      transformFrameRef.current = null;
      const pending = pendingTransformRef.current;
      pendingTransformRef.current = null;
      // 태블릿 드래그 중에는 React 전체 지도 트리를 다시 렌더링하지 않고
      // 합성 레이어의 transform만 갱신한다. 최종 상태는 pointerup에서 한 번 동기화한다.
      if (pending) paintScheduledTransform(pending);
    });
  }, [constrainTransform, paintScheduledTransform]);

  const flushScheduledTransform = useCallback(() => {
    if (transformFrameRef.current !== null) {
      window.cancelAnimationFrame(transformFrameRef.current);
      transformFrameRef.current = null;
    }
    const pending = pendingTransformRef.current;
    pendingTransformRef.current = null;
    setMapTransform(pending || transformRef.current);
  }, []);

  useEffect(() => () => {
    if (transformFrameRef.current !== null) {
      window.cancelAnimationFrame(transformFrameRef.current);
    }
  }, []);

  const getViewportPoint = useCallback((clientX, clientY) => {
    const viewport = mapViewportRef.current;
    const rect = viewport?.getBoundingClientRect?.();
    if (rect && rect.width > 0 && rect.height > 0) {
      if (uprightPreview) {
        return {
          x: (clientX - rect.left) * ((viewport.clientWidth || rect.width) / rect.width),
          y: (clientY - rect.top) * ((viewport.clientHeight || rect.height) / rect.height),
        };
      }
      // 전체 페이지가 -90도 회전되어 있으므로 화면 좌표를 지도 내부 좌표로 되돌린다.
      return {
        x: rect.bottom - clientY,
        y: clientX - rect.left,
      };
    }
    const { viewportWidth, viewportHeight } = getMapMetrics();
    return { x: viewportWidth / 2, y: viewportHeight / 2 };
  }, [getMapMetrics, uprightPreview]);

  const zoomAtViewportPoint = useCallback((requestedScale, point) => {
    const current = transformRef.current;
    const nextScale = Math.min(2.4, Math.max(1, Number(requestedScale) || 1));
    const { baseLeft, baseTop } = getMapMetrics();
    const mapX = (point.x - baseLeft - current.x) / Math.max(current.scale, 0.001);
    const mapY = (point.y - baseTop - current.y) / Math.max(current.scale, 0.001);
    setTransform({
      scale: nextScale,
      x: point.x - baseLeft - mapX * nextScale,
      y: point.y - baseTop - mapY * nextScale,
    });
  }, [getMapMetrics, setTransform]);

  const closeMissionSelection = useCallback(() => {
    setSelectedMissionId(null);
    onActivity();
  }, [onActivity]);

  const focusMission = useCallback((mission) => {
    if (mission.x === null || mission.y === null) return;
    const viewport = mapViewportRef.current;
    const {
      viewportWidth,
      viewportHeight,
      canvasWidth,
      canvasHeight,
      baseLeft,
      baseTop,
    } = getMapMetrics();
    // 선택 시 안쪽 지도 영역만 확대하면 사각형으로 크롭된 것처럼 보인다.
    // 지도 원본 비율을 유지하면서 화면 전체를 덮는 최소 배율까지 확대한다.
    const coverScale = Math.max(
      viewportWidth / Math.max(canvasWidth, 1),
      viewportHeight / Math.max(canvasHeight, 1)
    );
    const scale = Math.min(2.4, Math.max(1.65, coverScale));
    setTransform({
      scale,
      x: viewportWidth * 0.5 - baseLeft - mission.x * canvasWidth * scale,
      y: viewportHeight * 0.42 - baseTop - mission.y * canvasHeight * scale,
    });
  }, [getMapMetrics, setTransform]);

  const scrollCardIntoView = useCallback((index, behavior = 'smooth') => {
    const carousel = carouselRef.current;
    const card = carousel?.querySelector(`[data-mission-card-index="${index}"]`);
    if (!carousel || !card) return;
    // scrollIntoView는 overflow:hidden인 회전 캔버스까지 스크롤할 수 있다.
    // 카드 목록 한 곳만 이동하여 고정된 진행상황/층 버튼의 위치를 보존한다.
    const left = Math.max(0, card.offsetLeft + card.offsetWidth / 2 - carousel.clientWidth / 2);
    if (carousel.scrollTo) carousel.scrollTo({ left, behavior });
    else carousel.scrollLeft = left;
  }, []);

  const selectMission = useCallback((mission, { syncCarousel = true } = {}) => {
    const id = String(mission.spot_id);
    setSelectedMissionId(id);
    focusMission(mission);
    onActivity();
    if (syncCarousel) {
      const index = floorSpots.findIndex((spot) => String(spot.spot_id) === id);
      window.requestAnimationFrame(() => scrollCardIntoView(index, 'smooth'));
    }
  }, [floorSpots, focusMission, onActivity, scrollCardIntoView]);

  const changeFloor = useCallback((floor) => {
    if (floor === activeFloor || !floors.includes(floor)) return;
    setActiveFloor(floor);
    setSelectedMissionId(null);
    setTransform({ x: 0, y: 0, scale: 1 });
    onActivity();
  }, [activeFloor, floors, onActivity, setTransform]);

  useLayoutEffect(() => {
    if (!selectedMissionId) return;
    const index = floorSpots.findIndex((spot) => String(spot.spot_id) === selectedMissionId);
    if (index >= 0) scrollCardIntoView(index, 'auto');
  }, [floorSpots, scrollCardIntoView, selectedMissionId]);

  const moveToMission = (step) => {
    const currentFloorIndex = floorNavigationSpots.findIndex(
      (spot) => String(spot.spot_id) === selectedMissionId
    );
    const nextIndex = currentFloorIndex < 0
      ? floorNavigationSpots.findIndex((spot) => !spot.done)
      : (currentFloorIndex + step + floorNavigationSpots.length) % floorNavigationSpots.length;
    const mission = floorNavigationSpots[nextIndex < 0 ? 0 : nextIndex];
    if (!mission) return;
    selectMission(mission);
  };

  const handleCarouselScroll = () => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    window.requestAnimationFrame(() => {
      const center = carousel.scrollLeft + carousel.clientWidth / 2;
      let nearest = null;
      let distance = Number.POSITIVE_INFINITY;
      carousel.querySelectorAll('[data-mission-card-index]').forEach((card) => {
        const nextDistance = Math.abs(card.offsetLeft + card.clientWidth / 2 - center);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = Number(card.dataset.missionCardIndex);
        }
      });
      const mission = floorSpots[nearest];
      if (mission && String(mission.spot_id) !== selectedMissionId) {
        selectMission(mission, { syncCarousel: false });
      }
    });
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    didDragRef.current = false;
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      const center = getViewportPoint((a.x + b.x) / 2, (a.y + b.y) / 2);
      const metrics = getMapMetrics();
      const { baseLeft, baseTop } = metrics;
      const current = transformRef.current;
      pinchRef.current = {
        distance: Math.hypot(b.x - a.x, b.y - a.y),
        scale: current.scale,
        mapX: (center.x - baseLeft - current.x) / Math.max(current.scale, 0.001),
        mapY: (center.y - baseTop - current.y) / Math.max(current.scale, 0.001),
        metrics,
      };
      onActivity();
      return;
    }
    const viewport = mapViewportRef.current;
    const rect = viewport?.getBoundingClientRect?.();
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      x: transformRef.current.x,
      y: transformRef.current.y,
      metrics: getMapMetrics(),
      localScaleX: uprightPreview && rect?.width
        ? (viewport.clientWidth || rect.width) / rect.width
        : 1,
      localScaleY: uprightPreview && rect?.height
        ? (viewport.clientHeight || rect.height) / rect.height
        : 1,
    };
    onActivity();
  };

  const handlePointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      const center = getViewportPoint((a.x + b.x) / 2, (a.y + b.y) / 2);
      const nextScale = Math.min(2.4, Math.max(
        1,
        pinchRef.current.scale * (distance / Math.max(1, pinchRef.current.distance))
      ));
      const { baseLeft, baseTop } = pinchRef.current.metrics;
      scheduleTransform({
        scale: nextScale,
        x: center.x - baseLeft - pinchRef.current.mapX * nextScale,
        y: center.y - baseTop - pinchRef.current.mapY * nextScale,
      }, pinchRef.current.metrics);
      didDragRef.current = true;
      return;
    }
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const screenDx = event.clientX - drag.clientX;
    const screenDy = event.clientY - drag.clientY;
    if (Math.abs(screenDx) + Math.abs(screenDy) > 4) didDragRef.current = true;
    scheduleTransform({
      ...transformRef.current,
      // 실전 화면은 -90도 축을 되돌리고, 세로 미리보기는 축소 배율만 보정한다.
      x: uprightPreview ? drag.x + screenDx * drag.localScaleX : drag.x - screenDy,
      y: uprightPreview ? drag.y + screenDy * drag.localScaleY : drag.y + screenDx,
    }, drag.metrics);
  };

  const handlePointerEnd = (event) => {
    flushScheduledTransform();
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    onActivity();
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.18 : 0.18;
    zoomAtViewportPoint(
      transformRef.current.scale + delta,
      getViewportPoint(event.clientX, event.clientY)
    );
    onActivity();
  };

  const handleMapClick = (event) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (event.target.closest?.('.qs-progress-pin')) return;
    if (selectedMissionId) closeMissionSelection();
  };

  return (
    <section
      className={`qs-progress${selectedMission ? ' qs-progress--selected' : ''}`}
      data-testid="quest-headquarters-mission-map"
      onClick={(event) => event.stopPropagation()}
    >
      <header className="qs-progress-header">
        <span>미션 진행상황</span>
        <strong>{data.done}/{data.total}</strong>
      </header>

      {floors.length > 1 && (
        <nav className="qs-progress-floor-tabs" aria-label="미션 층 선택">
          {floors.map((floor) => (
            <button
              type="button"
              key={floor}
              className={floor === activeFloor ? 'is-active' : ''}
              aria-pressed={floor === activeFloor}
              onClick={() => changeFloor(floor)}
            >
              {floor}층
            </button>
          ))}
        </nav>
      )}

      <div
        className="qs-progress-map-area"
        ref={mapViewportRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onWheel={handleWheel}
        onClick={handleMapClick}
      >
        <div
          className="qs-progress-map-canvas"
          ref={mapCanvasRef}
          data-testid="quest-map-draggable-stage"
          style={{
            aspectRatio: `${mapWidth} / ${mapHeight}`,
            transform: `translate3d(${mapTransform.x}px, ${mapTransform.y}px, 0) scale(${mapTransform.scale})`,
            '--qs-map-inverse-scale': String(
              (1 + Math.max(0, mapTransform.scale - 1) * 0.16)
              / Math.max(mapTransform.scale, 0.001)
            ),
          }}
        >
          <div className={`qs-progress-map-image-frame${activeMap.imageWindow ? ' qs-progress-map-image-frame--full-art' : ''}`}>
          <img
            className="qs-progress-map-image"
            src={activeMap.image_url}
            style={headquartersMapImageStyle(activeMap)}
            alt={`${activeFloor}층 미션 지도`}
            draggable="false"
          />
          </div>
          {renderableSpots.map((spot) => {
            const selectedClusterMission = spot.missions.find(
              (mission) => String(mission.spot_id) === selectedMissionId
            );
            const containsSelectedMission = Boolean(selectedClusterMission);
            const missionToOpen = spot.missions[0];
            return (
              <button
                type="button"
                key={spot.marker_key}
                className={`qs-progress-pin${spot.done ? ' qs-progress-pin--done' : ''}${containsSelectedMission ? ' qs-progress-pin--selected' : ''}`}
                data-done={spot.done ? '1' : '0'}
                data-spot-id={spot.spot_id}
                data-cluster-count={spot.cluster_count}
                style={{
                  left: `${spot.x * 100}%`,
                  top: `${spot.y * 100}%`,
                  '--qs-pin-dx': '0px',
                  '--qs-pin-dy': '0px',
                }}
                title={spot.missions.map((mission) => mission.name).join(', ')}
                aria-label={spot.cluster_count > 1
                  ? `가까운 미션 ${spot.cluster_count}개`
                  : `${spot.name} ${spot.done ? '완료' : '미완료'}`}
                onPointerDown={(event) => {
                  didDragRef.current = false;
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  selectMission(missionToOpen);
                }}
              >
                <MissionProgressPin
                  done={selectedClusterMission?.done ?? spot.done}
                  count={spot.cluster_count}
                  missions={spot.missions}
                  selectedMissionId={selectedMissionId}
                />
              </button>
            );
          })}
        </div>
      </div>

      {selectedMission && (
        <div className="qs-mission-selection" data-testid="quest-mission-selection">
          {selectedNavigationIndex > 0 && (
            <button type="button" className="qs-mission-nav qs-mission-nav--previous" aria-label="이전 미션" onClick={() => moveToMission(-1)}>
              <ChevronIcon direction="left" />
            </button>
          )}
          <div className="qs-mission-carousel" ref={carouselRef} onScroll={handleCarouselScroll}>
            {floorSpots.map((mission, index) => (
              <article
                key={mission.spot_id}
                data-mission-card-index={index}
                data-selected={String(mission.spot_id) === selectedMissionId ? '1' : '0'}
                className="qs-mission-card"
                onClick={() => selectMission(mission)}
              >
                <span className="qs-mission-card-area">{data.questSetName || `${activeFloor}층 미션`}</span>
                <h2>{mission.name}</h2>
                <p>{String(mission.description || '').replace(/\\n/g, '\n')}</p>
              </article>
            ))}
          </div>
          {selectedNavigationIndex >= 0 && selectedNavigationIndex < floorNavigationSpots.length - 1 && (
            <button type="button" className="qs-mission-nav qs-mission-nav--next" aria-label="다음 미션" onClick={() => moveToMission(1)}>
              <ChevronIcon direction="right" />
            </button>
          )}
        </div>
      )}

    </section>
  );
}


// 색종이 조각 — 위치/지연/색을 한 번 계산해 재사용(렌더마다 재생성 방지).
const CONFETTI_PIECES = Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
  id: i,
  left: Math.round((i / CONFETTI_COUNT) * 100 + (i % 3) * 3) % 100,
  delay: (i % 10) * 0.12,
  duration: 2.4 + (i % 5) * 0.35,
  color: ['#fbbf24', '#f87171', '#34d399', '#60a5fa', '#c084fc', '#f472b6'][i % 6],
}));

export default function QuestStatusPage() {
  const [busy, setBusy] = useState(false); // status 조회 중
  const [result, setResult] = useState(null); // { kind, couponName?, remaining?, message? }
  const [animationKey, setAnimationKey] = useState(0); // 스캔당 1회 애니메이션 재생용
  const [missionMapData, setMissionMapData] = useState(null);
  const [missionMapConfig, setMissionMapConfig] = useState({ maps: [] });
  const [headquartersMaps, setHeadquartersMaps] = useState([]);
  const [missionFloorImage, setMissionFloorImage] = useState(null);
  const [rewardStepIndex, setRewardStepIndex] = useState(null);
  const [rewardDetails, setRewardDetails] = useState(EMPTY_REWARD_DETAILS);
  const [headquartersWaitingScreenUrl, setHeadquartersWaitingScreenUrl] = useState('');
  const [headquartersSetupLoading, setHeadquartersSetupLoading] = useState(true);
  const [rewardButtonPressed, setRewardButtonPressed] = useState(false);
  const [missionCompleteIntroFinished, setMissionCompleteIntroFinished] = useState(false);
  const [giftOpening, setGiftOpening] = useState(false);
  const [silverRewardPlaying, setSilverRewardPlaying] = useState(false);
  const [couponRevealPlaying, setCouponRevealPlaying] = useState(false);
  const [eggHatchPlaying, setEggHatchPlaying] = useState(false);
  const [animalRevealPlaying, setAnimalRevealPlaying] = useState(false);
  const [animalRevealComplete, setAnimalRevealComplete] = useState(false);
  const [devUprightPreview, setDevUprightPreview] = useState(false);
  const cooldownMap = useRef(new Map());
  const resultTimerRef = useRef(null);
  const missionMapTimerRef = useRef(null);
  const rewardButtonTimerRef = useRef(null);
  const missionCompleteVideoRef = useRef(null);
  const giftOpeningVideoRef = useRef(null);
  const silverRewardVideoRef = useRef(null);
  const couponRevealVideoRef = useRef(null);
  const eggHatchVideoRef = useRef(null);
  const animalRevealVideoRef = useRef(null);
  const giftOpeningRef = useRef(false);
  const silverRewardPlayingRef = useRef(false);
  const couponRevealPlayingRef = useRef(false);
  const eggHatchPlayingRef = useRef(false);
  const animalRevealPlayingRef = useRef(false);
  const animalRevealCompleteRef = useRef(false);
  const missionCompleteIntroFinishedRef = useRef(false);
  const missionCompleteIntroTimerRef = useRef(null);
  const giftOpeningFallbackTimerRef = useRef(null);
  const homeExitTapsRef = useRef([]);
  const allowBackNavigationRef = useRef(false);
  const animalReveal = useMemo(
    () => resolveQuestAnimalReveal(rewardDetails),
    [rewardDetails.animalId, rewardDetails.animalName, rewardDetails.animalVideoUrl]
  );
  const hasAnimalRewardProfile = Boolean(
    rewardDetails.animalProfileId
    || rewardDetails.animalId
    || rewardDetails.animalName
    || rewardDetails.animalImageUrl
    || rewardDetails.animalVideoUrl
  );
  const shouldPresentAnimalReward = shouldPresentQuestAnimalReward(
    rewardDetails.animalRewardStatus
  ) && (!rewardDetails.animalRewardStatus || hasAnimalRewardProfile);

  /*
   * 현장 태블릿의 가장자리 뒤로가기 제스처는 WebView history.back()으로 들어온다.
   * 같은 URL의 보호 엔트리를 한 장 더 쌓아 뒤로가도 라우트가 바뀌지 않게 하고,
   * 운영자가 왼쪽 위를 3초 안에 5회 터치하거나 개발 도구의 홈 버튼을 누른 경우에만
   * 서비스 메뉴로 나간다.
   */
  useEffect(() => {
    const lockedUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const guardState = { ...(window.history.state || {}), questStatusBackGuard: true };
    window.history.pushState(guardState, '', lockedUrl);

    const keepQuestStatusOpen = () => {
      if (allowBackNavigationRef.current) return;
      window.history.pushState(guardState, '', lockedUrl);
    };

    window.addEventListener('popstate', keepQuestStatusOpen);
    return () => window.removeEventListener('popstate', keepQuestStatusOpen);
  }, []);

  // 주키퍼 본부는 하루 종일 켜 두는 화면이므로 기동 시 한 번만 읽지 않고 유휴 중에도
  // 활성 세트 설정을 갱신한다. 최초 조회 중에는 미설정/실패 안내를 표시하지 않는다.
  useEffect(() => {
    let cancelled = false;
    const loadWaitingScreen = async () => {
      try {
        const setup = await getQuestSetup();
        if (cancelled) return;
        const imageUrl = extractHeadquartersWaitingScreenUrl(setup);
        const mapItems = extractHeadquartersMaps(setup);
        setHeadquartersWaitingScreenUrl(imageUrl);
        setHeadquartersMaps(mapItems);
        if (imageUrl) {
          const preloadImage = new Image();
          preloadImage.src = imageUrl;
        }
        mapItems.forEach((item) => {
          const preloadImage = new Image();
          preloadImage.src = item.image_url;
        });
      } catch {
        // 네트워크 순간 실패 때 이미 표시 중인 대기화면을 지우지 않는다.
      } finally {
        if (!cancelled) setHeadquartersSetupLoading(false);
      }
    };
    loadWaitingScreen();
    const interval = setInterval(loadWaitingScreen, HEADQUARTERS_SETUP_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const exitToServiceMenu = useCallback((event) => {
    event?.stopPropagation();
    homeExitTapsRef.current = [];
    allowBackNavigationRef.current = true;
    window.history.replaceState({}, '', '/');
    window.dispatchEvent(new Event('popstate'));
  }, []);

  const handleHiddenHomeExitTap = useCallback((event) => {
    event.stopPropagation();
    const now = Date.now();
    homeExitTapsRef.current = [
      ...homeExitTapsRef.current.filter((tappedAt) => (
        now - tappedAt <= HOME_EXIT_TAP_WINDOW_MS
      )),
      now,
    ];
    if (homeExitTapsRef.current.length < HOME_EXIT_TAP_COUNT) return;

    exitToServiceMenu(event);
  }, [exitToServiceMenu]);

  const scheduleMissionMapReset = useCallback(() => {
    if (missionMapTimerRef.current) clearTimeout(missionMapTimerRef.current);
    missionMapTimerRef.current = setTimeout(() => {
      missionMapTimerRef.current = null;
      setMissionMapData(null);
      setRewardDetails(EMPTY_REWARD_DETAILS);
    }, MISSION_MAP_DISPLAY_MS);
  }, []);

  useEffect(() => {
    REWARD_FLOW.forEach(({ image, headlineImage, heroImage, buttonImage }) => {
      [image, headlineImage, heroImage, buttonImage].filter(Boolean).forEach((source) => {
        const preloadImage = new Image();
        preloadImage.src = source;
      });
    });
    const speechBubble = new Image();
    speechBubble.src = headquartersRewardSpeechBubble;

    return () => {
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      if (missionMapTimerRef.current) clearTimeout(missionMapTimerRef.current);
      if (rewardButtonTimerRef.current) clearTimeout(rewardButtonTimerRef.current);
      if (missionCompleteIntroTimerRef.current) {
        clearTimeout(missionCompleteIntroTimerRef.current);
      }
      if (giftOpeningFallbackTimerRef.current) clearTimeout(giftOpeningFallbackTimerRef.current);
    };
  }, []);

  // 매장별 층 지도 설정만 미리 읽는다. 공통 배경은 사용하지 않는다.
  // 실제 미션 현황 조회 시 최신 설정을 다시 받는다.
  useEffect(() => {
    const storeId = localStorage.getItem('store_id');
    if (!storeId) return undefined;

    let cancelled = false;
    api.get('/webapp/missions/maps', { params: { store_id: storeId } })
      .then((response) => {
        if (cancelled) return;
        const nextConfig = {
          maps: Array.isArray(response?.maps) ? response.maps : [],
        };
        setMissionMapConfig(nextConfig);
      })
      .catch(() => {
        // 사전 조회 실패가 실제 QR 미션 현황 표시를 막아서는 안 된다.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!missionMapData) return undefined;
    scheduleMissionMapReset();

    return () => {
      if (missionMapTimerRef.current) clearTimeout(missionMapTimerRef.current);
      missionMapTimerRef.current = null;
    };
  }, [missionMapData, scheduleMissionMapReset]);

  const advanceRewardScreen = () => {
    if (rewardStepIndex === null || REWARD_FLOW[rewardStepIndex].waitForConfirmation) return;
    const nextStepIndex = Math.min(rewardStepIndex + 1, REWARD_FLOW.length - 1);
    if (REWARD_FLOW[nextStepIndex]?.id === 'gift-friend' && !shouldPresentAnimalReward) {
      setRewardStepIndex(null);
      setRewardDetails(EMPTY_REWARD_DETAILS);
      return;
    }
    setRewardStepIndex(nextStepIndex);
  };

  useEffect(() => {
    if (rewardStepIndex === 0) return;
    giftOpeningRef.current = false;
    silverRewardPlayingRef.current = false;
    couponRevealPlayingRef.current = false;
    eggHatchPlayingRef.current = false;
    setGiftOpening(false);
    setSilverRewardPlaying(false);
    setCouponRevealPlaying(false);
    setEggHatchPlaying(false);
    const preserveAnimalReveal = rewardStepIndex === 3
      && (animalRevealPlayingRef.current || animalRevealCompleteRef.current);
    if (!preserveAnimalReveal) {
      animalRevealPlayingRef.current = false;
      animalRevealCompleteRef.current = false;
      setAnimalRevealPlaying(false);
      setAnimalRevealComplete(false);
    }
    if (!preserveAnimalReveal && giftOpeningFallbackTimerRef.current) {
      clearTimeout(giftOpeningFallbackTimerRef.current);
      giftOpeningFallbackTimerRef.current = null;
    }
  }, [rewardStepIndex]);

  const keepMissionMapOpen = useCallback(() => {
    scheduleMissionMapReset();
  }, [scheduleMissionMapReset]);

  const returnToIdle = useCallback(() => {
    [
      resultTimerRef,
      missionMapTimerRef,
      rewardButtonTimerRef,
      missionCompleteIntroTimerRef,
      giftOpeningFallbackTimerRef,
    ].forEach((timerRef) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    });

    giftOpeningRef.current = false;
    silverRewardPlayingRef.current = false;
    couponRevealPlayingRef.current = false;
    eggHatchPlayingRef.current = false;
    animalRevealPlayingRef.current = false;
    animalRevealCompleteRef.current = false;
    missionCompleteIntroFinishedRef.current = false;

    setResult(null);
    setMissionMapData(null);
    setRewardStepIndex(null);
    setRewardDetails(EMPTY_REWARD_DETAILS);
    setRewardButtonPressed(false);
    setMissionCompleteIntroFinished(false);
    setGiftOpening(false);
    setSilverRewardPlaying(false);
    setCouponRevealPlaying(false);
    setEggHatchPlaying(false);
    setAnimalRevealPlaying(false);
    setAnimalRevealComplete(false);
  }, []);

  const isCooldown = (qrCode) => {
    const last = cooldownMap.current.get(qrCode);
    return !!last && Date.now() - last < COOLDOWN_MS;
  };

  const showResult = useCallback((next) => {
    setResult(next);
    setAnimationKey((k) => k + 1); // 동일 결과여도 애니메이션 재생
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
    resultTimerRef.current = setTimeout(() => setResult(null), RESULT_DISPLAY_MS);
  }, []);

  // ── QR 스캔 → 상태 조회 ───────────────────────────────────
  const handleScan = useCallback(
    async (qrCode) => {
      if (busy) return;
      if (isCooldown(qrCode)) return;
      cooldownMap.current.set(qrCode, Date.now());

      setBusy(true);
      setRewardDetails(EMPTY_REWARD_DETAILS);
      try {
        // 평소에는 읽기전용 mission-map 1회만 호출한다. 완료 가능성이 있거나 지도 데이터가
        // 없을 때만 status 를 추가 호출해 정산 상태·폴백 문구를 확인한다.
        const [mapResult, memberResult] = await Promise.allSettled([
          fetchQuestMissionMap(qrCode),
          getMemberByQR(qrCode, false),
        ]);
        const mapData = mapResult.status === 'fulfilled' ? mapResult.value : null;

        let interactiveMapData = null;
        const memberId = memberResult.status === 'fulfilled'
          ? memberResult.value?.member?.member_id
          : null;
        const storeId = localStorage.getItem('store_id');
        if (memberId && storeId) {
          try {
            const [progress, latestMapConfig] = await Promise.all([
              api.get('/webapp/missions/progress', {
                params: { store_id: storeId, member_id: memberId },
              }),
              api.get('/webapp/missions/maps', { params: { store_id: storeId } }),
            ]);
            const normalizedMapConfig = {
              maps: latestMapConfig?.maps || [],
            };
            interactiveMapData = createInteractiveMissionMap(
              progress,
              withHeadquartersMapOverride(normalizedMapConfig, headquartersMaps),
              memberId
            );
            if (interactiveMapData) setMissionMapConfig(normalizedMapConfig);
          } catch {
            // 상세 조회가 실패해도 기존 tools 전용 mission-map 응답으로 계속 표시한다.
          }
        }

        const displayMapData = interactiveMapData || mapData;
        const hasMissionMap = interactiveMapData ? true : canRenderStatusMap(mapData);
        const mapIsComplete = hasMissionMap
          && Number(displayMapData.total) > 0
          && Number(displayMapData.done) >= Number(displayMapData.total);

        if (mapIsComplete) {
          const [statusResult, setupResult] = await Promise.allSettled([
            getQuestStatus(qrCode),
            getQuestSetup(),
          ]);
          const statusData = statusResult.status === 'fulfilled' ? statusResult.value : null;
          const setup = setupResult.status === 'fulfilled' ? setupResult.value : null;
          rememberQuestTestQr('complete', qrCode);
          setResult(null);
          setMissionMapData(null);
          setRewardDetails(mergeRewardDetails(
            extractRewardDetails(setup),
            extractRewardDetails(statusData)
          ));
          setRewardStepIndex(0);
          return;
        }

        if (hasMissionMap) {
          rememberQuestTestQr('incomplete', qrCode);
          setResult(null);
          setRewardStepIndex(null);
          setMissionMapData(displayMapData);
          return;
        }

        const data = (await getQuestStatus(qrCode)) || {};
        if (data.active_set === false) {
          showResult({ kind: 'no-set' });
        } else if (data.completed) {
          const setup = await getQuestSetup().catch(() => null);
          rememberQuestTestQr('complete', qrCode);
          setResult(null);
          setMissionMapData(null);
          setRewardDetails(mergeRewardDetails(
            extractRewardDetails(setup),
            extractRewardDetails(data)
          ));
          setRewardStepIndex(0);
        } else {
          rememberQuestTestQr('incomplete', qrCode);
          // 지도·좌표가 없는 지점의 안전 폴백.
          const remaining =
            Number.isFinite(data.remaining) && data.remaining > 0 ? data.remaining : 1;
          showResult({ kind: 'incomplete', remaining });
        }
      } catch (err) {
        // 비회원 QR 등 — 친절한 안내 메시지.
        showResult({ kind: 'error', message: err?.message || '회원 QR 을 다시 확인해 주세요.' });
      } finally {
        setBusy(false);
      }
    },
    [busy, headquartersMaps, missionMapConfig, showResult]
  );

  // USB QR 리더기 / 네이티브 키오스크 스캔 — 광장 TV도 현장 입력은 USB 리더기(카메라는 HTTPS 폴백).
  useUsbQrReader({
    enabled: !busy && !result && !missionMapData && rewardStepIndex === null,
    onScan: (raw) => {
      // 퀘스트 전용 합성 파서 — 여권(ZLP_)까지 받는다. 공유 extractQRCode 는 넓히지 않는다(S10)
      const code = extractQuestScanCode(raw);
      return code ? handleScan(code) : undefined;
    },
  });

  // 이 페이지는 **화면 아무 데나** 눌러도 전체화면으로 갔다(광장 TV 운영용). 웹(크롬)에서
  // 미리 보다가 클릭하면 갑자기 전체화면이 되어 빠져나오는 법을 모르므로,
  // **태블릿 앱에서만** 동작시킨다 (2026-08-06 요구). 현장 TV 는 앱이라 영향 없다.
  const toggleFullscreen = () => {
    if (!isBridgeApp()) return;
    const el = document.documentElement;
    (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
  };

  const handlePageClick = () => {
    toggleFullscreen();
  };

  const clearGiftOpeningFallback = () => {
    if (giftOpeningFallbackTimerRef.current) {
      clearTimeout(giftOpeningFallbackTimerRef.current);
      giftOpeningFallbackTimerRef.current = null;
    }
  };

  const finishMissionCompleteIntro = useCallback(() => {
    if (missionCompleteIntroFinishedRef.current) return;
    missionCompleteIntroFinishedRef.current = true;
    if (missionCompleteIntroTimerRef.current) {
      clearTimeout(missionCompleteIntroTimerRef.current);
      missionCompleteIntroTimerRef.current = null;
    }

    missionCompleteVideoRef.current?.pause();
    const cubeVideo = giftOpeningVideoRef.current;
    if (cubeVideo) {
      cubeVideo.pause();
      cubeVideo.currentTime = 0;
    }
    setMissionCompleteIntroFinished(true);
  }, []);

  useEffect(() => {
    if (rewardStepIndex !== 0) {
      missionCompleteIntroFinishedRef.current = false;
      setMissionCompleteIntroFinished(false);
      return undefined;
    }

    missionCompleteIntroFinishedRef.current = false;
    setMissionCompleteIntroFinished(false);
    missionCompleteIntroTimerRef.current = setTimeout(
      finishMissionCompleteIntro,
      MISSION_COMPLETE_INTRO_FALLBACK_MS
    );

    const video = missionCompleteVideoRef.current;
    if (!video) {
      finishMissionCompleteIntro();
      return undefined;
    }

    video.currentTime = 0;
    try {
      const playback = video.play();
      playback?.catch(finishMissionCompleteIntro);
    } catch {
      finishMissionCompleteIntro();
    }

    return () => {
      if (missionCompleteIntroTimerRef.current) {
        clearTimeout(missionCompleteIntroTimerRef.current);
        missionCompleteIntroTimerRef.current = null;
      }
    };
  }, [finishMissionCompleteIntro, rewardStepIndex]);

  const finishSilverReward = () => {
    if (!silverRewardPlayingRef.current) return;
    silverRewardPlayingRef.current = false;
    clearGiftOpeningFallback();
    setSilverRewardPlaying(false);
    setRewardStepIndex((index) => (index === 0 ? 1 : index));
  };

  const startSilverReward = () => {
    giftOpeningRef.current = false;
    clearGiftOpeningFallback();
    setGiftOpening(false);
    silverRewardPlayingRef.current = true;
    setSilverRewardPlaying(true);
    giftOpeningFallbackTimerRef.current = setTimeout(
      finishSilverReward,
      SILVER_REWARD_FALLBACK_MS
    );

    const video = silverRewardVideoRef.current;
    if (!video) {
      finishSilverReward();
      return;
    }

    video.currentTime = 0;
    try {
      const playback = video.play();
      playback?.catch(finishSilverReward);
    } catch {
      finishSilverReward();
    }
  };

  const finishGiftOpening = () => {
    if (!giftOpeningRef.current) return;
    startSilverReward();
  };

  const finishCouponReveal = () => {
    if (!couponRevealPlayingRef.current) return;
    couponRevealPlayingRef.current = false;
    clearGiftOpeningFallback();
    setCouponRevealPlaying(false);
    setRewardStepIndex((index) => (index === 1 ? 2 : index));
  };

  const startCouponReveal = () => {
    clearGiftOpeningFallback();
    couponRevealPlayingRef.current = true;
    setCouponRevealPlaying(true);
    giftOpeningFallbackTimerRef.current = setTimeout(
      finishCouponReveal,
      COUPON_REVEAL_FALLBACK_MS
    );

    const video = couponRevealVideoRef.current;
    if (!video) {
      finishCouponReveal();
      return;
    }

    video.currentTime = 0;
    try {
      const playback = video.play();
      playback?.catch(finishCouponReveal);
    } catch {
      finishCouponReveal();
    }
  };

  const finishEggHatch = () => {
    if (!eggHatchPlayingRef.current) return;
    eggHatchPlayingRef.current = false;
    clearGiftOpeningFallback();
    setEggHatchPlaying(false);
    setRewardStepIndex(3);
    startAnimalReveal();
  };

  const startEggHatch = () => {
    clearGiftOpeningFallback();
    eggHatchPlayingRef.current = true;
    setEggHatchPlaying(true);
    giftOpeningFallbackTimerRef.current = setTimeout(
      finishEggHatch,
      EGG_HATCH_FALLBACK_MS
    );

    const video = eggHatchVideoRef.current;
    if (!video) {
      finishEggHatch();
      return;
    }

    video.currentTime = 0;
    try {
      const playback = video.play();
      playback?.catch(finishEggHatch);
    } catch {
      finishEggHatch();
    }
  };

  const finishAnimalReveal = () => {
    if (!animalRevealPlayingRef.current) return;
    animalRevealPlayingRef.current = false;
    animalRevealCompleteRef.current = true;
    clearGiftOpeningFallback();

    const video = animalRevealVideoRef.current;
    if (video) {
      video.pause();
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = Math.max(0, video.duration - 0.04);
      }
    }

    setAnimalRevealPlaying(false);
    setAnimalRevealComplete(true);
  };

  const startAnimalReveal = () => {
    clearGiftOpeningFallback();
    animalRevealCompleteRef.current = false;
    animalRevealPlayingRef.current = true;
    setAnimalRevealComplete(false);
    setAnimalRevealPlaying(true);

    if (!animalReveal?.video) {
      finishAnimalReveal();
      return;
    }

    giftOpeningFallbackTimerRef.current = setTimeout(
      finishAnimalReveal,
      animalReveal.fallbackMs
    );

    const video = animalRevealVideoRef.current;
    if (!video) {
      finishAnimalReveal();
      return;
    }

    video.currentTime = 0;
    try {
      const playback = video.play();
      playback?.catch(finishAnimalReveal);
    } catch {
      finishAnimalReveal();
    }
  };

  const handleImageActionClick = (event) => {
    event.stopPropagation();
    if (rewardButtonTimerRef.current) return;

    const startedAnimalReveal = currentScreen.id === 'gift-friend'
      && !animalRevealCompleteRef.current;
    setRewardButtonPressed(true);
    if (currentScreen.id === 'mission-complete' && missionCompleteIntroFinishedRef.current) {
      giftOpeningRef.current = true;
      setGiftOpening(true);
      giftOpeningFallbackTimerRef.current = setTimeout(
        finishGiftOpening,
        CUBE_ROTATION_FALLBACK_MS
      );

      const video = giftOpeningVideoRef.current;
      if (!video) {
        finishGiftOpening();
      } else {
        video.currentTime = 0;
        try {
          const playback = video.play();
          playback?.catch(finishGiftOpening);
        } catch {
          finishGiftOpening();
        }
      }
    } else if (currentScreen.id === 'gift-coin') {
      startCouponReveal();
    } else if (currentScreen.id === 'gift-coupon' && shouldPresentAnimalReward) {
      startEggHatch();
    } else if (currentScreen.id === 'gift-friend' && !animalRevealCompleteRef.current) {
      startAnimalReveal();
    }
    rewardButtonTimerRef.current = setTimeout(() => {
      rewardButtonTimerRef.current = null;
      setRewardButtonPressed(false);
      if (currentScreen.id === 'mission-complete') return;
      if (currentScreen.id === 'gift-coin') return;
      if (currentScreen.id === 'gift-coupon' && eggHatchPlayingRef.current) return;
      if (currentScreen.id === 'gift-friend') {
        if (startedAnimalReveal) return;
        if (animalRevealCompleteRef.current) {
          animalRevealCompleteRef.current = false;
          setAnimalRevealComplete(false);
          setRewardStepIndex(null);
          setRewardDetails(EMPTY_REWARD_DETAILS);
        }
        return;
      }
      if (currentScreen.waitForConfirmation) {
        setRewardStepIndex(null);
        setRewardDetails(EMPTY_REWARD_DETAILS);
      } else {
        advanceRewardScreen();
      }
    }, REWARD_BUTTON_PRESS_MS);
  };

  // 로컬 완료 미리보기는 서버 상태를 변경하지 않고 완료 분기 화면부터 바로 보여준다.
  const handleTestMemberClick = async (event, memberId, forceComplete = false) => {
    event.stopPropagation();
    if (busy) return;

    setBusy(true);
    try {
      if (forceComplete) {
        const setup = await getQuestSetup().catch(() => null);
        setResult(null);
        setMissionMapData(null);
        setRewardDetails(extractRewardDetails(setup));
        setRewardStepIndex(0);
        return;
      }

      const storeId = localStorage.getItem('store_id');
      if (!storeId) throw new Error('매장 정보를 확인할 수 없습니다.');

      const [progress, latestMapConfig, setup] = await Promise.all([
        api.get('/webapp/missions/progress', {
          params: { store_id: storeId, member_id: memberId },
        }),
        api.get('/webapp/missions/maps', { params: { store_id: storeId } }),
        getQuestSetup().catch(() => null),
      ]);
      const normalizedMapConfig = {
        maps: latestMapConfig?.maps || [],
      };
      const interactiveMapData = createInteractiveMissionMap(
        progress,
        withHeadquartersMapOverride(normalizedMapConfig, setup ? extractHeadquartersMaps(setup) : headquartersMaps),
        memberId
      );
      if (!interactiveMapData) throw new Error('진행 중인 미션 정보를 찾을 수 없습니다.');

      setMissionMapConfig(normalizedMapConfig);
      setResult(null);
      setRewardStepIndex(null);
      setRewardDetails(extractRewardDetails(setup));
      setMissionMapData(interactiveMapData);
    } catch (err) {
      showResult({
        kind: 'error',
        message: err?.message || '미션 현황을 불러오지 못했습니다.',
      });
    } finally {
      setBusy(false);
    }
  };

  // ── 결과 오버레이 ─────────────────────────────────────────
  const renderResult = () => {
    if (!result) return null;

    if (result.kind === 'complete') {
      return (
        <div className="qs-overlay qs-complete" key={animationKey} data-testid="quest-status-complete">
          <div className="qs-confetti" aria-hidden="true">
            {CONFETTI_PIECES.map((p) => (
              <span
                key={p.id}
                className="qs-confetti-piece"
                style={{
                  left: `${p.left}%`,
                  background: p.color,
                  animationDelay: `${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                }}
              />
            ))}
          </div>
          <div className="qs-complete-inner">
            <div className="qs-complete-emoji">🎉</div>
            <h1 className="qs-complete-title">퀘스트 완료~!</h1>
            <p className="qs-complete-coupon">{result.rewardLabel} 획득!</p>
          </div>
        </div>
      );
    }

    if (result.kind === 'incomplete') {
      return (
        <div className="qs-overlay qs-incomplete" key={animationKey} data-testid="quest-status-incomplete">
          <div className="qs-incomplete-emoji">🌱</div>
          <h1 className="qs-incomplete-title">
            아직 <span className="qs-remaining-num">{result.remaining}</span>개 남았어~!
          </h1>
          <p className="qs-incomplete-sub">스팟을 모두 돌면 보상이 짠~ 하고 나타나요!</p>
        </div>
      );
    }

    if (result.kind === 'no-set') {
      return (
        <div className="qs-overlay qs-neutral" key={animationKey} data-testid="quest-status-no-set">
          <div className="qs-neutral-emoji">🌳</div>
          <h1 className="qs-neutral-title">진행 중인 퀘스트가 없습니다</h1>
        </div>
      );
    }

    // error
    return (
      <div className="qs-overlay qs-neutral" key={animationKey} data-testid="quest-status-error">
        <div className="qs-neutral-emoji">🤔</div>
        <h1 className="qs-neutral-title">{result.message}</h1>
      </div>
    );
  };

  const currentScreen = rewardStepIndex === null
    ? IDLE_SCREEN
    : REWARD_FLOW[rewardStepIndex];
  const rewardVideoOnlyPlaying = silverRewardPlaying
    || couponRevealPlaying
    || eggHatchPlaying
    || animalRevealPlaying;
  const showRewardButton = !rewardVideoOnlyPlaying
    && Boolean(currentScreen.buttonImage && currentScreen.actionLabel)
    && (currentScreen.id !== 'mission-complete'
      || (missionCompleteIntroFinished && !giftOpening));
  const showAnimalProfileFallback = currentScreen.id === 'gift-friend'
    && animalRevealComplete
    && !animalReveal?.video
    && Boolean(rewardDetails.animalImageUrl);
  const currentRewardHeadline = rewardHeadline(currentScreen, rewardDetails);
  const currentStepId = missionMapData ? 'mission-progress' : currentScreen.id;
  const currentScreenImage = currentScreen.id === 'idle' && headquartersWaitingScreenUrl
    ? headquartersWaitingScreenUrl
    : currentScreen.image;
  const ambientImage = missionMapData
    ? missionFloorImage
    : currentScreenImage;

  return (
    <div className={`qs-viewport${missionMapData ? ' qs-viewport--mission-map' : ''}`}>
      {ambientImage && (
        <div className="qs-ambient-background" aria-hidden="true">
          <img src={ambientImage} alt="" draggable="false" />
        </div>
      )}
      <button
        type="button"
        className="qs-hidden-home-exit"
        aria-label="운영 메뉴로 돌아가기"
        onClick={handleHiddenHomeExitTap}
        data-testid="quest-hidden-home-exit"
      />
      {SHOW_QUEST_TEST_CONTROLS && (
        <aside className="qs-dev-toolbar" aria-label="주키퍼 본부 분기 테스트">
          <div className="qs-dev-toolbar-heading">
            <span className="qs-dev-toolbar-badge">DEV</span>
            <strong>주키퍼 본부 테스트</strong>
            <button
              type="button"
              className="qs-dev-menu-home"
              aria-label="태블릿 기능 메뉴로 이동"
              data-testid="quest-dev-menu-home"
              onClick={exitToServiceMenu}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 10.8 12 3l9 7.8v9.7a.5.5 0 0 1-.5.5h-5.7v-6.3H9.2V21H3.5a.5.5 0 0 1-.5-.5v-9.7Z" />
              </svg>
              <span>홈</span>
            </button>
          </div>
          <div className="qs-dev-toolbar-actions">
            {currentStepId === 'idle' && (
              <>
                <button
                  type="button"
                  className="qs-dev-tool-button qs-dev-qr-button qs-dev-qr-button--incomplete"
                  onClick={(event) => handleTestMemberClick(event, QUEST_TEST_MEMBERS.incomplete)}
                >
                  <span className="qs-dev-tool-status" aria-hidden="true" />
                  미완료 회원
                </button>
                <button
                  type="button"
                  className="qs-dev-tool-button qs-dev-qr-button qs-dev-qr-button--complete"
                  onClick={(event) => handleTestMemberClick(
                    event,
                    QUEST_TEST_MEMBERS.incomplete,
                    true
                  )}
                >
                  <span className="qs-dev-tool-check" aria-hidden="true">✓</span>
                  완료 회원
                </button>
              </>
            )}
            {currentStepId !== 'idle' && (
              <button
                type="button"
                className="qs-dev-tool-button qs-dev-home-button"
                aria-label="주키퍼 본부 대기 화면으로 돌아가기"
                onClick={(event) => {
                  event.stopPropagation();
                  returnToIdle();
                }}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 10.8 12 3l9 7.8v9.7a.5.5 0 0 1-.5.5h-5.7v-6.3H9.2V21H3.5a.5.5 0 0 1-.5-.5v-9.7Z" />
                </svg>
                <span>대기 화면</span>
              </button>
            )}
            <button
              type="button"
              className={`qs-dev-tool-button qs-dev-orientation-toggle${devUprightPreview ? ' is-upright' : ''}`}
              aria-label={devUprightPreview ? '실전 회전 화면으로 보기' : '세로 화면으로 보기'}
              aria-pressed={devUprightPreview}
              onClick={(event) => {
                event.stopPropagation();
                setDevUprightPreview((upright) => !upright);
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.7 6.3A8 8 0 1 0 20 12h-2a6 6 0 1 1-1.76-4.24L13 11h8V3l-3.3 3.3Z" />
              </svg>
              <span>{devUprightPreview ? '실전 회전' : '세로 미리보기'}</span>
            </button>
          </div>
        </aside>
      )}
      <div
        className={`qs-page${devUprightPreview ? ' qs-page--dev-upright' : ''}`}
        onClick={handlePageClick}
        data-screen-step={currentStepId}
      >
        {/* 대기 화면 (스캐너) — 결과 표시 중에는 결과 오버레이가 덮음 */}
        <div className="qs-idle">
          {missionMapData ? (
            <MissionProgressScreen
              data={missionMapData}
              onMapImageChange={setMissionFloorImage}
              uprightPreview={devUprightPreview}
              onActivity={keepMissionMapOpen}
            />
          ) : (
            <div className="qs-screen-frame">
              {currentScreenImage ? (
                <img
                  className="qs-idle-image"
                  src={currentScreenImage}
                  alt={currentScreen.alt}
                  draggable="false"
                  onError={currentScreen.id === 'idle' && headquartersWaitingScreenUrl
                    ? () => setHeadquartersWaitingScreenUrl('')
                    : undefined}
                />
              ) : currentScreen.id === 'idle' && headquartersSetupLoading ? (
                <div className="qs-waiting-screen-loading" data-testid="quest-headquarters-waiting-loading" aria-hidden="true" />
              ) : (
                <div className="qs-waiting-screen-error" data-testid="quest-headquarters-waiting-error">
                  주키퍼 본부 대기화면을 불러올 수 없습니다.
                </div>
              )}
              {currentScreen.id === 'mission-complete' && (
                <>
                  {rewardDetails.missionCompleteVideoUrl && (
                    <video
                      ref={missionCompleteVideoRef}
                      className={`qs-gift-opening-video qs-mission-complete-intro-video${missionCompleteIntroFinished ? '' : ' is-playing'}`}
                      data-testid="quest-mission-complete-video"
                      src={rewardDetails.missionCompleteVideoUrl}
                      playsInline
                      preload="auto"
                      aria-hidden="true"
                      onEnded={finishMissionCompleteIntro}
                      onError={finishMissionCompleteIntro}
                    />
                  )}
                  {rewardDetails.rewardBoxVideoUrl && (
                    <video
                      ref={giftOpeningVideoRef}
                      className={`qs-gift-opening-video qs-cube-rotation-video${giftOpening ? ' is-playing' : ''}${missionCompleteIntroFinished && !giftOpening ? ' is-ready' : ''}`}
                      data-testid="quest-gift-opening-video"
                      src={rewardDetails.rewardBoxVideoUrl}
                      playsInline
                      preload="auto"
                      aria-hidden="true"
                      onLoadedData={(event) => {
                        if (giftOpeningRef.current) return;
                        event.currentTarget.pause();
                        event.currentTarget.currentTime = 0;
                      }}
                      onEnded={finishGiftOpening}
                      onError={finishGiftOpening}
                    />
                  )}
                  {rewardDetails.coinSilverVideoUrl && (
                    <video
                      ref={silverRewardVideoRef}
                      className={`qs-gift-opening-video qs-silver-reward-video${silverRewardPlaying ? ' is-playing' : ''}`}
                      data-testid="quest-silver-reward-video"
                      src={rewardDetails.coinSilverVideoUrl}
                      playsInline
                      preload="auto"
                      aria-hidden="true"
                      onEnded={finishSilverReward}
                      onError={finishSilverReward}
                    />
                  )}
                </>
              )}
              {currentScreen.id === 'gift-coin' && rewardDetails.couponVideoUrl && (
                <video
                  ref={couponRevealVideoRef}
                  className={`qs-gift-opening-video qs-coupon-reveal-video${couponRevealPlaying ? ' is-playing' : ''}`}
                  data-testid="quest-coupon-reveal-video"
                  src={rewardDetails.couponVideoUrl}
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                  onEnded={finishCouponReveal}
                  onError={finishCouponReveal}
                />
              )}
              {currentScreen.id === 'gift-coupon' && rewardDetails.eggHatchVideoUrl && (
                <video
                  ref={eggHatchVideoRef}
                  className={`qs-gift-opening-video qs-egg-hatch-video${eggHatchPlaying ? ' is-playing' : ''}`}
                  data-testid="quest-egg-hatch-video"
                  src={rewardDetails.eggHatchVideoUrl}
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                  onEnded={finishEggHatch}
                  onError={finishEggHatch}
                />
              )}
              {animalReveal?.video && (currentScreen.id === 'gift-friend' || eggHatchPlaying) && (
                <video
                  ref={animalRevealVideoRef}
                  className={`qs-gift-opening-video qs-animal-reveal-video${animalRevealPlaying ? ' is-playing' : ''}${animalRevealComplete ? ' is-final' : ''}`}
                  data-testid="quest-animal-reveal-video"
                  data-animal-reveal-key={animalReveal.key}
                  src={animalReveal.video}
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                  onEnded={finishAnimalReveal}
                  onError={finishAnimalReveal}
                />
              )}
              {showAnimalProfileFallback && (
                <div
                  className="qs-animal-profile-fallback"
                  data-testid="quest-animal-profile-fallback"
                >
                  <img
                    src={rewardDetails.animalImageUrl}
                    alt={rewardDetails.animalName || '획득한 동물친구'}
                    draggable="false"
                  />
                </div>
              )}
              {!rewardVideoOnlyPlaying && currentScreen.headlineImage && (
                <img
                  key={`${currentScreen.id}-headline`}
                  className={`qs-reward-headline-image qs-reward-headline-image--${currentScreen.heroKind}`}
                  src={currentScreen.headlineImage}
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                />
              )}
              {!rewardVideoOnlyPlaying && currentScreen.dynamicHeadline && (
                <div
                  key={`${currentScreen.id}-dynamic-headline`}
                  className={`qs-reward-dynamic-headline qs-reward-dynamic-headline--${currentScreen.heroKind}`}
                  data-testid={`quest-reward-headline-${currentScreen.dynamicHeadline}`}
                >
                  <img
                    className="qs-reward-dynamic-headline-bubble"
                    src={headquartersRewardSpeechBubble}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                  <p className="qs-reward-dynamic-headline-text">{currentRewardHeadline}</p>
                </div>
              )}
              {!rewardVideoOnlyPlaying
                && (!animalRevealComplete
                  || (!animalReveal?.video && !rewardDetails.animalImageUrl))
                && currentScreen.heroImage && (
                <div
                  key={`${currentScreen.id}-hero`}
                  className={`qs-reward-hero qs-reward-hero--${currentScreen.heroKind}${currentScreen.tada ? ' qs-reward-hero--tada' : ''}${giftOpening ? ' qs-reward-hero--opening' : ''}`}
                >
                  <img
                    className="qs-reward-hero-image"
                    src={currentScreen.heroImage}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                </div>
              )}
              {showRewardButton && (
                <button
                  type="button"
                  className={`qs-reward-button-visual qs-image-action${rewardButtonPressed ? ' is-pressed' : ''}`}
                  aria-label={currentScreen.actionLabel}
                  aria-busy={rewardButtonPressed}
                  onClick={handleImageActionClick}
                >
                  <img
                    className="qs-reward-button-image"
                    src={currentScreen.buttonImage}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                </button>
              )}
            </div>
          )}
          {busy && <p className="qs-idle-busy">확인 중…</p>}
        </div>

        {renderResult()}
      </div>
    </div>
  );
}
